import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";

import { useAppSelector } from "~/app/store";
import { WorkstationChangesToolbar } from "~/features/preview/workstation/components/WorkstationChangesToolbar";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { allTemperatureZones } from "~/lib/tempZone";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { useGetAutostoreGridQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useGetWorkstationPickingConfigurationQuery,
  useGetWorkstationsQuery
} from "~/redux/warehouse/workstation.hooks";
import {
  TemperatureZone,
  UpdateWorkstation,
  WorkstationSummaryDto
} from "~/types/api";

export type WorkstationSettingsFormValues = UpdateWorkstation & {
  temperatureZones: TemperatureZone[];
};

export type WorkstationConfigFormReturn =
  UseFormReturn<WorkstationSettingsFormValues> & {
    pickingTempZoneOptions: TemperatureZone[];
    canEditPickingTempZones: boolean;
  };

const EMPTY_FORM_VALUES: WorkstationSettingsFormValues = {
  deviceId: "",
  autostoreGridId: "",
  orientation: "",
  roles: [],
  multiPortEnabled: false,
  ports: [],
  totePlacements: [],
  workstationId: "",
  mode: "",
  pickableOrderTypes: [],
  temperatureZones: []
};

/**
 * RTK/Immer may freeze workstation rows from the cache. react-hook-form mutates
 * nested values in place (ports[], totePlacements[].totePosition, etc.), so defaults
 * must be deep-cloned mutable copies.
 */
function mutableWorkstationFormValues(
  workstation: WorkstationSummaryDto | undefined,
  temperatureZones: TemperatureZone[] = []
): WorkstationSettingsFormValues {
  if (!workstation?.id) {
    return structuredClone(EMPTY_FORM_VALUES);
  }
  return structuredClone({
    deviceId: workstation.deviceId || "",
    autostoreGridId: workstation.autostoreGridId || "",
    orientation: workstation.orientation || "",
    roles: workstation.roles ? [...workstation.roles] : [],
    multiPortEnabled: workstation.multiPortEnabled || false,
    ports: workstation.ports ? [...workstation.ports] : [],
    totePlacements: workstation.totePlacements
      ? [...workstation.totePlacements]
      : [],
    workstationId: workstation.id,
    mode: workstation.mode || "",
    pickableOrderTypes: workstation.pickableOrderTypes
      ? [...workstation.pickableOrderTypes]
      : [],
    temperatureZones: [...temperatureZones]
  });
}

export const useWorkstationConfigForm = ({
  viewTitle
}: {
  viewTitle: ViewNameTranslation | undefined;
}): WorkstationConfigFormReturn => {
  const { setToolbar } = useNavbar({ viewTitle });
  useGetWorkstationsQuery();
  const workstation = useAppSelector(selectDropdownWorkstation);
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const loadedPickingConfigForWsRef = useRef<string | null>(null);

  const gridId = workstation?.autostoreGridId;
  const { data: autostoreGrid } = useGetAutostoreGridQuery(gridId ?? skipToken);
  const gridTempZones = autostoreGrid?.temperatureZones ?? [];
  const isMultiTempGrid = gridTempZones.length > 1;
  const pickingTempZoneOptions: TemperatureZone[] =
    gridTempZones.length > 0 ? gridTempZones : allTemperatureZones;
  const canEditPickingTempZones = isMultiTempGrid;

  const pickingConfigArgs =
    gridId && workstation?.id
      ? { autostoreGridId: gridId, workstationId: workstation.id }
      : skipToken;

  const { data: pickingConfig, isSuccess: isPickingConfigSuccess } =
    useGetWorkstationPickingConfigurationQuery(pickingConfigArgs, {
      refetchOnMountOrArgChange: true
    });

  const defaultValues = useMemo(
    (): WorkstationSettingsFormValues =>
      mutableWorkstationFormValues(workstation),
    [workstation]
  );

  const form = useForm<WorkstationSettingsFormValues>({ defaultValues });
  const {
    formState: { isDirty },
    reset
  } = form;

  useEffect(() => {
    loadedPickingConfigForWsRef.current = null;
    reset(mutableWorkstationFormValues(workstation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstation?.id, reset]);

  useEffect(() => {
    if (!workstation?.id || !isPickingConfigSuccess) {
      return;
    }
    const loadedZones = pickingConfig?.configuration.temperatureZones;
    const loadedForWorkstationId = pickingConfig?.configuration.workstationId;
    if (loadedForWorkstationId !== workstation.id) {
      return;
    }
    if (loadedPickingConfigForWsRef.current === workstation.id) {
      return;
    }
    loadedPickingConfigForWsRef.current = workstation.id;
    reset(mutableWorkstationFormValues(workstation, [...(loadedZones ?? [])]));
  }, [
    workstation,
    isPickingConfigSuccess,
    pickingConfig?.configuration.temperatureZones,
    pickingConfig?.configuration.workstationId,
    reset
  ]);

  useEffect(() => {
    if (isDirty && isAdmin) {
      setToolbar(
        <FormProvider {...form}>
          <WorkstationChangesToolbar viewTitle={viewTitle} />
        </FormProvider>
      );
    } else {
      setToolbar(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form omitted (unstable identity)
  }, [isDirty, isAdmin, setToolbar, viewTitle]);

  return {
    ...form,
    pickingTempZoneOptions,
    canEditPickingTempZones
  };
};
